// Bergerie pèlerine sainte Marguerite — public page with hospitality request form.
// Content extracted from "2023 08 01 Bergerie pèlerine sainte Marguerite V1.pptx".
import { useEffect, useState } from "react";
import { useI18n } from "@/i18n/I18nContext";
import Ornament from "@/components/Ornament";
import api from "@/lib/api";

const SEO_TITLES = {
  fr: "Bergerie pèlerine sainte Marguerite — Hospitalité sur le Chemin du Mont saint Michel",
  en: "Saint Margaret Pilgrim Sheepfold — Hospitality on the Way of Mont saint Michel",
  it: "Ovile pellegrino di Santa Margherita — Ospitalità sul Cammino di Mont saint Michel",
  es: "Aprisco peregrino de Santa Margarita — Hospitalidad en el Camino del Mont saint Michel",
  de: "Pilger-Schäferei der heiligen Margareta — Gastfreundschaft auf dem Weg zum Mont saint Michel",
  pt: "Aprisco peregrino de Santa Margarida — Hospitalidade no Caminho do Monte Saint-Michel",
  zh: "圣玛格丽特朝圣者牧场 — 圣米歇尔山朝圣之路上的接待",
  pl: "Aprysk pielgrzymi św. Małgorzaty — Gościnność na Drodze do Mont saint Michel",
  ru: "Паломнический овчарник святой Маргариты — Гостеприимство на Пути к Мон-Сен-Мишель",
};
const SEO_DESC = {
  fr: "Dernière hospitalité sur le Chemin du Mont saint Michel. 12 nattes dans le grenier à foin, pièce d'eau, esprit pèlerin. Demande de séjour en ligne.",
  en: "The last hospitality on the Way of Mont saint Michel. 12 mats in the hayloft, shared bathroom, pilgrim spirit. Online stay request.",
  it: "L'ultima ospitalità sul Cammino di Mont saint Michel. 12 stuoie nel fienile, bagno comune, spirito pellegrino.",
  es: "La última hospitalidad en el Camino del Mont saint Michel. 12 esterillas en el henil, baño común, espíritu peregrino.",
  de: "Die letzte Gastfreundschaft auf dem Weg zum Mont saint Michel. 12 Matten im Heuboden, Gemeinschaftsbad, Pilgergeist.",
  pt: "A última hospitalidade no Caminho do Monte Saint-Michel. 12 esteiras no palheiro, casa de banho comum, espírito peregrino.",
  zh: "圣米歇尔山朝圣之路上的最后接待。干草棚内 12 张草垫,公共浴室,朝圣精神。",
  pl: "Ostatnia gościnność na Drodze do Mont saint Michel. 12 mat na strychu, wspólna łazienka, duch pielgrzyma.",
  ru: "Последнее гостеприимство на Пути к Мон-Сен-Мишель. 12 циновок на сеновале, общая ванная, паломнический дух. Онлайн-заявка.",
};
import SeoHead from "@/components/SeoHead";

const MAX_BEDS = 12;

// --- Multilingual content -------------------------------------------------
const T = {
  fr: {
    kicker: "Hospitalité · Chemin du Mont saint Michel",
    title: "Bergerie pèlerine sainte Marguerite",
    subtitle: "Réouverture de la dernière Hospitalité sur le Chemin du Mont saint Michel",
    introQuote: "Là où l'expérience sensorielle du Profane rejoint le Sacré.",
    context: {
      kicker: "Esprit du lieu",
      title: "Une halte sur le chemin",
      body: "À mi-distance entre Caen et le Mont saint Michel, le Prieuré saint Léonard se dresse depuis l'époque mérovingienne sur le promontoire qui domine la baie. Lieu de paix, d'écoute et de liberté intérieure, il accueille à nouveau le pèlerin sur la « via Anglica », au croisement de la route des Ducs venue de Caen et de la Via Angelica venue du Mont Gargano. La Bergerie pèlerine sainte Marguerite y rouvre ses portes — comme dernière hospitalité avant la traversée vers le Mont.",
    },
    history: {
      kicker: "Histoire et racines",
      title: "Une tradition d'accueil millénaire",
      body: "L'accueil des pèlerins et des marcheurs au Prieuré saint Léonard est attesté dès 1025, dernière halte avant la traversée vers le Mont. En 1087, Guillaume le Conquérant donne ces terres à l'abbaye Saint-Étienne de Caen ; les bergers de la baie — gardiens du sel et des troupeaux — recueillaient déjà les miquelots sur le chemin du sable. Le plan terrier de 1634 atteste l'existence de la bergerie. Aujourd'hui, sur ce même socle de pierre, l'hospitalité reprend vie.",
    },
    fonctionnementTitle: "Fonctionnement",
    fonctionnementItems: [
      { h: "Couchage",       body: "12 nattes dans le grenier à foin" },
      { h: "Pièce d'eau",    body: "Une pièce d'eau commune : douche, lavabo, toilettes" },
      { h: "Saison",         body: "Ouverte du 1ᵉʳ avril au 1ᵉʳ novembre" },
      { h: "Donativo",       body: "Contribution libre suggérée 10 à 15 €/nuit, à régler sur place" },
      { h: "Hospitalier",    body: "Un hospitalier de la commune assure l'accueil et l'entretien" },
      { h: "Gestion",        body: "Portée par l'association « Les Amis du Prieuré saint Léonard »" },
    ],
    contributionsTitle: "Adhésion · Dons · Services",
    contributionsIntro: "Pour soutenir la vie de la Bergerie au-delà du donativo, plusieurs voies vous sont ouvertes :",
    contributionsItems: [
      { h: "Adhésion à l'association", body: "Plein 50 € · Couple 30 € · Tarif réduit 20 € · Scouts & Guides 20 € (une par patrouille/équipe/clan)." },
      { h: "Don libre", body: "Un don du montant de votre choix, par chèque ou en ligne." },
      { h: "Services", body: "Contribution en services : entretien, jardinage, accueil, traductions, photos, etc. Écrivez-nous." },
    ],
    taxDeductible: "L'association est d'intérêt général : adhésions et dons ouvrent droit à un reçu fiscal (réduction d'impôt de 66 % dans la limite de 20 % du revenu imposable, art. 200 du CGI).",
    membershipCta: "Adhérer / Faire un don",
    rulesTitle: "Règlement intérieur de la Bergerie",
    rulesIntro: "L'hospitalité s'inscrit dans la tradition des hospitalités de pèlerinage. Pour préserver la sérénité du lieu et le respect de chacun, voici les règles partagées par les hôtes et les pèlerins.",
    rulesSections: [
      { h: "Accueil & arrivée", items: [
        "Demande préalable obligatoire via le formulaire, avec attestation d'adhésion.",
        "Arrivée à partir de 16 h, départ avant 10 h le lendemain matin.",
        "Présentation d'une pièce d'identité à l'arrivée pour le registre d'hospitalité.",
        "Durée du séjour limitée en principe à 2 nuits consécutives, sauf accord préalable.",
      ]},
      { h: "Vie commune", items: [
        "Silence respecté à partir de 22 h et jusqu'à 7 h, en hommage à la tradition monastique.",
        "Le grenier à foin est un couchage mixte sur nattes ; chacun veille à la pudeur et au calme de ses voisins.",
        "Pièce d'eau, couloirs et cuisine sont partagés : nettoyés après usage.",
        "Les chaussures se déposent à l'entrée. Les bâtons de pèlerin se rangent dans le porte-bâtons.",
      ]},
      { h: "Cuisine, repas, hygiène", items: [
        "La cuisine commune est à votre libre usage — avec souci des autres.",
        "Vaisselle lavée, séchée, rangée immédiatement après le repas.",
        "Tri sélectif respecté (bacs sur place).",
        "Pas d'alcool fort dans les espaces communs ; vin de table modéré aux repas.",
      ]},
      { h: "Engagements du pèlerin", items: [
        "Respecter la vocation spirituelle du lieu — Prieuré et Bergerie ne sont pas des locations touristiques.",
        "Participer simplement à la vie commune (vaisselle, rangement, petite tâche).",
        "Signaler tout problème (matériel, sanitaire, sécurité) sans délai à l'hospitalier.",
        "Pas d'animaux à l'intérieur (sauf chien-guide), pas de tabac dans les locaux.",
      ]},
      { h: "Donativo & dons", items: [
        "La nuitée n'a pas de prix fixé. Contribution libre suggérée : 10 à 15 €/nuit, à déposer dans la boîte prévue.",
        "Tout don, en numéraire ou par chèque, donne lieu sur demande à un reçu fiscal (Art. 200 du CGI).",
        "Les services rendus à la Bergerie (entretien, accueil, traductions, photos) sont également les bienvenus.",
      ]},
      { h: "Sécurité", items: [
        "Extincteurs et plan d'évacuation affichés.",
        "Issue de secours à l'étage.",
        "Numéros d'urgence : 15 (SAMU) · 18 (Pompiers) · 112 (Europe).",
      ]},
      { h: "Manquement", items: [
        "Tout manquement aux présentes règles peut entraîner le départ immédiat du pèlerin, à l'appréciation de l'hospitalier et du Conseil d'administration de l'association.",
      ]},
    ],
    rulesShow: "Lire le règlement intérieur",
    rulesHide: "Masquer le règlement",
    partnersTitle: "Partenaires & réseaux",
    partners: [
      "Association « Les Chemins du Mont saint Michel »",
      "Abbayes de Normandie",
      "Fédération européenne saint Léonard",
      "Abbaye San Leonardo di Siponto (Foggia, Italie)",
    ],
    budgetTitle: "Budget de restauration",
    budgetTotal: "32 000 €",
    budgetItems: [
      { lbl: "Toiture en chaume",        v: "12 000 €" },
      { lbl: "Charpente",                v: "5 000 €" },
      { lbl: "Pièce d'eau (douche, lavabo, WC)", v: "4 000 €" },
      { lbl: "Cuisine & équipement",     v: "4 000 €" },
      { lbl: "Nattes & couchage (grenier à foin)", v: "4 000 €" },
      { lbl: "Aménagements divers",      v: "3 000 €" },
    ],
    // Form -----------------------------------------------------------------
    formKicker: "Hospitalité dans la bergerie",
    formTitle: "Demande de séjour",
    formIntro: "Manifestez votre intérêt pour un accueil à la Bergerie pèlerine. Notre hospitalier reviendra vers vous pour confirmer la disponibilité. Capacité : 12 nattes dans le grenier à foin.",
    conditionsTitle: "Conditions d'accueil",
    conditions: [
      "Être adhérent aux Amis du Prieuré saint Léonard.",
      "Guides & scouts : une adhésion suffit par patrouille, équipe ou clan.",
      "Adultes : adhésion individuelle requise pour chaque participant majeur.",
      "Capacité maximale : 12 personnes (12 nattes dans le grenier à foin, une pièce d'eau commune).",
    ],
    fields: {
      firstName: "Prénom", lastName: "Nom", email: "E-mail", phone: "Téléphone (optionnel)",
      group: "Groupe / Patrouille / Équipe / Clan", groupHelp: "Laissez vide pour un séjour individuel ou en famille",
      nbPeople: "Nombre de personnes", nbNights: "Nombre de nuits",
      arrivalDate: "Date d'arrivée souhaitée (optionnel)",
      notes: "Notes / précisions (optionnel)",
      member: "J'atteste être adhérent aux Amis du Prieuré saint Léonard (ou que mon groupe est adhérent).",
    },
    cta: "Envoyer la demande",
    sending: "Envoi en cours…",
    successTitle: "Demande reçue, merci !",
    successBody: "Notre hospitalier reviendra vers vous très prochainement. Un e-mail de confirmation vient de vous être envoyé.",
    capacityWarn: "Le nombre de personnes ne peut excéder 12 (capacité du grenier à foin).",
    donativoBadge: "DONATIVO · 10–15 €/nuit indicatif",
  },
  en: {
    kicker: "Hospitality · Way of Mont saint Michel",
    title: "Saint Margaret Pilgrim Sheepfold",
    subtitle: "Reopening the last Hospitality on the Way of Mont saint Michel",
    introQuote: "Where the sensory experience of the Profane meets the Sacred.",
    context: {
      kicker: "Spirit of the place",
      title: "A halt on the pilgrim's way",
      body: "Halfway between Caen and Mont saint Michel, the Priory of Saint Léonard rises since the Merovingian era on the promontory overlooking the bay. A place of peace, listening and inner freedom, it welcomes again the pilgrim on the « via Anglica », at the crossroads of the Dukes' way from Caen and the Via Angelica from Mont Gargano. The Saint Margaret Pilgrim Sheepfold reopens its doors there — as the last hospitality before crossing to the Mount.",
    },
    history: {
      kicker: "History and roots",
      title: "A thousand-year tradition of welcome",
      body: "The welcoming of pilgrims and walkers at the Priory of Saint Léonard is attested from 1025, last halt before crossing to the Mount. In 1087, William the Conqueror gave these lands to Saint-Étienne Abbey of Caen; the shepherds of the bay — guardians of salt and flocks — were already welcoming the miquelots on the sand path. The 1634 cadastral map attests to the sheepfold's existence. Today, on this same stone foundation, hospitality revives.",
    },
    fonctionnementTitle: "Operation",
    fonctionnementItems: [
      { h: "Sleeping",    body: "12 mats in the hayloft" },
      { h: "Bathroom",    body: "A shared bathroom: shower, sink, WC" },
      { h: "Season",      body: "Open from 1 April to 1 November" },
      { h: "Donativo",    body: "Free suggested contribution €10–15 per night, paid on site" },
      { h: "Host",        body: "A village host ensures welcome and upkeep" },
      { h: "Management",  body: "Carried by the « Friends of the Priory of Saint Léonard » association" },
    ],
    contributionsTitle: "Membership · Donations · Services",
    contributionsIntro: "To support the life of the Sheepfold beyond the donativo, several paths are open:",
    contributionsItems: [
      { h: "Membership", body: "Full €50 · Couple €30 · Reduced €20 · Scouts & Guides €20 (one per patrol/team/clan)." },
      { h: "Free donation", body: "A donation of the amount of your choice, by cheque or online." },
      { h: "Services", body: "Contribution in services: maintenance, gardening, hosting, translations, photos, etc. Please write to us." },
    ],
    taxDeductible: "The association is recognised as being of general interest: memberships and donations entitle French tax residents to a tax receipt (66% income tax reduction within 20% of taxable income, Art. 200 of the French Tax Code).",
    membershipCta: "Join / Donate",
    rulesTitle: "Sheepfold's house rules",
    rulesIntro: "Hospitality follows the tradition of pilgrim hospitalities. To preserve the serenity of the place and the respect of everyone, here are the rules shared by hosts and pilgrims.",
    rulesSections: [
      { h: "Welcome & arrival", items: [
        "Mandatory prior request via the form, with proof of membership.",
        "Arrival from 4 pm, departure before 10 am next morning.",
        "ID required on arrival for the hospitality register.",
        "Stay limited in principle to 2 consecutive nights, except by prior agreement.",
      ]},
      { h: "Common life", items: [
        "Silence respected from 10 pm to 7 am, honouring the monastic tradition.",
        "The hayloft is a mixed sleeping space on mats; modesty and quietness are everyone's responsibility.",
        "Bathroom, hallways and kitchen are shared spaces: cleaned after use.",
        "Shoes left at the entrance. Pilgrim staffs go in the staff-rack.",
      ]},
      { h: "Kitchen, meals, hygiene", items: [
        "The common kitchen is free to use — with consideration for others.",
        "Dishes washed, dried, stored immediately after the meal.",
        "Selective sorting respected (bins on site).",
        "No spirits in common areas; moderate table wine accepted at meals.",
      ]},
      { h: "Pilgrim's commitments", items: [
        "Respect the spiritual vocation of the place — not a tourist rental.",
        "Participate simply in common life (dishes, tidying, small tasks).",
        "Report any issue (equipment, sanitary, safety) without delay to the host.",
        "No animals indoors (except guide dogs), no smoking inside.",
      ]},
      { h: "Donativo & donations", items: [
        "The night has no fixed price. Suggested free contribution: €10–15 per night, in the dedicated box.",
        "Any donation, in cash or by cheque, entitles on request to a French tax receipt (Art. 200 CGI).",
        "Services to the Sheepfold (maintenance, hosting, translations, photos) are equally welcome.",
      ]},
      { h: "Safety", items: [
        "Fire extinguishers and evacuation plan posted.",
        "Emergency exit upstairs.",
        "Emergency numbers: 15 (SAMU) · 18 (Fire) · 112 (Europe).",
      ]},
      { h: "Breach", items: [
        "Any breach of these rules may result in immediate departure of the pilgrim, at the discretion of the host and of the association's Board.",
      ]},
    ],
    rulesShow: "Read the house rules",
    rulesHide: "Hide rules",
    partnersTitle: "Partners & networks",
    partners: [
      "« Ways of Mont saint Michel » association",
      "Abbeys of Normandy",
      "European Federation of Saint Léonard",
      "San Leonardo di Siponto Abbey (Foggia, Italy)",
    ],
    budgetTitle: "Restoration budget",
    budgetTotal: "€32,000",
    budgetItems: [
      { lbl: "Thatched roof",            v: "€12,000" },
      { lbl: "Framing",                  v: "€5,000" },
      { lbl: "Bathroom (shower, sink, WC)", v: "€4,000" },
      { lbl: "Kitchen & equipment",      v: "€4,000" },
      { lbl: "Mats & bedding (hayloft)", v: "€4,000" },
      { lbl: "Miscellaneous",            v: "€3,000" },
    ],
    formKicker: "Hospitality at the Sheepfold",
    formTitle: "Stay request",
    formIntro: "Express your interest in staying at the Pilgrim Sheepfold. Our host will get back to you to confirm availability. Capacity: 12 mats in the hayloft.",
    conditionsTitle: "Welcome conditions",
    conditions: [
      "Be a member of the Friends of the Priory of Saint Léonard.",
      "Guides & scouts: one membership suffices per patrol, team or clan.",
      "Adults: individual membership required for each adult participant.",
      "Maximum capacity: 12 persons (12 mats in the hayloft, one shared bathroom).",
    ],
    fields: {
      firstName: "First name", lastName: "Last name", email: "E-mail", phone: "Phone (optional)",
      group: "Group / Patrol / Team / Clan", groupHelp: "Leave empty for an individual or family stay",
      nbPeople: "Number of persons", nbNights: "Number of nights",
      arrivalDate: "Desired arrival date (optional)",
      notes: "Notes (optional)",
      member: "I certify being a member of the Friends of the Priory of Saint Léonard (or that my group is a member).",
    },
    cta: "Send request",
    sending: "Sending…",
    successTitle: "Request received, thank you!",
    successBody: "Our host will get back to you very soon. A confirmation e-mail has just been sent to you.",
    capacityWarn: "The number of persons cannot exceed 12 (hayloft capacity).",
    donativoBadge: "DONATIVO · €10–15/night suggested",
  },
};
// Italian, Spanish, German, Portuguese — fall back to FR copy with translated keys for the form-critical UI
T.it = {
  ...T.fr,
  kicker: "Ospitalità · Cammino di Mont saint Michel",
  title: "Ovile pellegrino di Santa Margherita",
  subtitle: "Riapertura dell'ultima Ospitalità sul Cammino di Mont saint Michel",
  formKicker: "Ospitalità all'ovile",
  formTitle: "Richiesta di soggiorno",
  formIntro: "Manifestate il vostro interesse per un soggiorno all'Ovile pellegrino. Il nostro ospitaliero vi risponderà per confermare la disponibilità. Capacità: 12 stuoie nel fienile.",
  conditionsTitle: "Condizioni di accoglienza",
  conditions: [
    "Essere soci degli Amici del Priorato di San Leonardo.",
    "Guide & scout: una sola iscrizione per pattuglia, squadra o clan.",
    "Adulti: iscrizione individuale richiesta per ogni partecipante maggiorenne.",
    "Capacità massima: 12 persone (12 stuoie nel fienile, un bagno comune).",
  ],
  fields: {
    firstName: "Nome", lastName: "Cognome", email: "E-mail", phone: "Telefono (facoltativo)",
    group: "Gruppo / Pattuglia / Squadra / Clan", groupHelp: "Lasciare vuoto per un soggiorno individuale o familiare",
    nbPeople: "Numero di persone", nbNights: "Numero di notti",
    arrivalDate: "Data di arrivo desiderata (facoltativa)",
    notes: "Note (facoltativo)",
    member: "Attesto di essere socio degli Amici del Priorato di San Leonardo (o che il mio gruppo lo è).",
  },
  cta: "Invia la richiesta", sending: "Invio in corso…",
  successTitle: "Richiesta ricevuta, grazie!",
  successBody: "Il nostro ospitaliero vi risponderà molto presto. Vi è stata appena inviata una e-mail di conferma.",
  capacityWarn: "Il numero di persone non può superare 12 (capacità del fienile).",
  donativoBadge: "DONATIVO · 10–15 €/notte indicativo",
};
T.es = {
  ...T.fr,
  kicker: "Hospitalidad · Camino del Mont saint Michel",
  title: "Aprisco peregrino de Santa Margarita",
  subtitle: "Reapertura de la última Hospitalidad en el Camino del Mont saint Michel",
  formKicker: "Hospitalidad en el aprisco",
  formTitle: "Solicitud de estancia",
  formIntro: "Manifieste su interés por una estancia en el Aprisco peregrino. Nuestro hospitalero le responderá para confirmar la disponibilidad. Capacidad: 12 esterillas en el henil.",
  conditionsTitle: "Condiciones de acogida",
  conditions: [
    "Ser miembro de los Amigos del Priorato de San Leonardo.",
    "Guías y scouts: una sola membresía por patrulla, equipo o clan.",
    "Adultos: membresía individual requerida para cada participante adulto.",
    "Capacidad máxima: 12 personas (12 esterillas en el henil, un baño común).",
  ],
  fields: {
    firstName: "Nombre", lastName: "Apellido", email: "Correo electrónico", phone: "Teléfono (opcional)",
    group: "Grupo / Patrulla / Equipo / Clan", groupHelp: "Dejar vacío para una estancia individual o familiar",
    nbPeople: "Número de personas", nbNights: "Número de noches",
    arrivalDate: "Fecha de llegada deseada (opcional)",
    notes: "Notas (opcional)",
    member: "Certifico ser miembro de los Amigos del Priorato de San Leonardo (o que mi grupo lo es).",
  },
  cta: "Enviar la solicitud", sending: "Enviando…",
  successTitle: "Solicitud recibida, ¡gracias!",
  successBody: "Nuestro hospitalero le responderá muy pronto. Acaba de recibir un correo electrónico de confirmación.",
  capacityWarn: "El número de personas no puede exceder de 12 (capacidad del henil).",
  donativoBadge: "DONATIVO · 10–15 €/noche indicativo",
};
T.de = {
  ...T.fr,
  kicker: "Gastfreundschaft · Weg zum Mont saint Michel",
  title: "Pilger-Schäferei der heiligen Margareta",
  subtitle: "Wiedereröffnung der letzten Pilgerherberge auf dem Weg zum Mont saint Michel",
  formKicker: "Gastfreundschaft in der Schäferei",
  formTitle: "Aufenthaltsanfrage",
  formIntro: "Bekunden Sie Ihr Interesse an einem Aufenthalt in der Pilger-Schäferei. Unser Gastgeber wird sich zur Bestätigung der Verfügbarkeit melden. Kapazität: 12 Matten im Heuboden.",
  conditionsTitle: "Aufnahmebedingungen",
  conditions: [
    "Mitglied der Freunde des Priorats Saint Léonard sein.",
    "Pfadfinder & Scouts: eine Mitgliedschaft pro Patrouille, Team oder Clan genügt.",
    "Erwachsene: Einzelmitgliedschaft für jeden volljährigen Teilnehmer erforderlich.",
    "Maximale Kapazität: 12 Personen (12 Matten im Heuboden, ein gemeinsamer Waschraum).",
  ],
  fields: {
    firstName: "Vorname", lastName: "Nachname", email: "E-Mail", phone: "Telefon (optional)",
    group: "Gruppe / Patrouille / Team / Clan", groupHelp: "Bei Einzel- oder Familienaufenthalt leer lassen",
    nbPeople: "Anzahl Personen", nbNights: "Anzahl Nächte",
    arrivalDate: "Gewünschtes Anreisedatum (optional)",
    notes: "Anmerkungen (optional)",
    member: "Ich bestätige, Mitglied der Freunde des Priorats Saint Léonard zu sein (oder dass meine Gruppe Mitglied ist).",
  },
  cta: "Anfrage senden", sending: "Wird gesendet…",
  successTitle: "Anfrage eingegangen, danke!",
  successBody: "Unser Gastgeber wird sich in Kürze melden. Eine Bestätigungs-E-Mail wurde Ihnen gerade zugesandt.",
  capacityWarn: "Die Personenzahl darf 12 nicht überschreiten (Kapazität des Heubodens).",
  donativoBadge: "DONATIVO · 10–15 €/Nacht Richtwert",
};
T.pt = {
  ...T.fr,
  kicker: "Hospitalidade · Caminho do Monte Saint-Michel",
  title: "Aprisco peregrino de Santa Margarida",
  subtitle: "Reabertura da última Hospitalidade no Caminho do Monte Saint-Michel",
  formKicker: "Hospitalidade no aprisco",
  formTitle: "Pedido de estadia",
  formIntro: "Manifeste o seu interesse por uma estadia no Aprisco peregrino. O nosso hospitaleiro responder-lhe-á para confirmar a disponibilidade. Capacidade: 12 esteiras no palheiro.",
  conditionsTitle: "Condições de acolhimento",
  conditions: [
    "Ser sócio dos Amigos do Priorado de São Leonardo.",
    "Guias & escuteiros: uma única adesão por patrulha, equipa ou clã.",
    "Adultos: adesão individual exigida para cada participante maior.",
    "Capacidade máxima: 12 pessoas (12 esteiras no palheiro, uma casa de banho comum).",
  ],
  fields: {
    firstName: "Nome próprio", lastName: "Apelido", email: "E-mail", phone: "Telefone (opcional)",
    group: "Grupo / Patrulha / Equipa / Clã", groupHelp: "Deixe vazio para estadia individual ou em família",
    nbPeople: "Número de pessoas", nbNights: "Número de noites",
    arrivalDate: "Data de chegada pretendida (opcional)",
    notes: "Notas (opcional)",
    member: "Atesto ser sócio dos Amigos do Priorado de São Leonardo (ou que o meu grupo é sócio).",
  },
  cta: "Enviar o pedido", sending: "A enviar…",
  successTitle: "Pedido recebido, obrigado!",
  successBody: "O nosso hospitaleiro responder-lhe-á muito em breve. Acaba de receber um e-mail de confirmação.",
  capacityWarn: "O número de pessoas não pode exceder 12 (capacidade do palheiro).",
  donativoBadge: "DONATIVO · 10–15 €/noite indicativo",
};
// Russian — full content translation
T.ru = {
  kicker: "Гостеприимство · Путь к Мон-Сен-Мишель",
  title: "Паломнический овчарник святой Маргариты",
  subtitle: "Открытие последнего пункта гостеприимства на Пути к Мон-Сен-Мишель",
  introQuote: "Там, где чувственный опыт мирского встречается со Священным.",
  context: {
    kicker: "Дух места",
    title: "Остановка на пути",
    body: "На полпути между Каном и Мон-Сен-Мишелем приорство Сен-Леонар возвышается со времён Меровингов на мысе, нависающем над заливом. Место мира, слушания и внутренней свободы вновь принимает паломника на « via Anglica » — на перекрёстке Пути герцогов из Кана и Via Angelica с горы Гаргано. Паломнический овчарник святой Маргариты вновь открывает свои двери — как последнее гостеприимство перед переходом к Мону.",
  },
  history: {
    kicker: "История и корни",
    title: "Тысячелетняя традиция гостеприимства",
    body: "Приём паломников и пеших путников в приорстве Сен-Леонар засвидетельствован с 1025 года — последняя остановка перед переходом к Мону. В 1087 году Вильгельм Завоеватель передал эти земли аббатству Сент-Этьен в Кане; пастухи залива — хранители соли и стад — уже принимали микелотов на песчаной тропе. Кадастр 1634 года подтверждает существование овчарника. Сегодня на этом же каменном основании гостеприимство возрождается.",
  },
  fonctionnementTitle: "Устройство",
  fonctionnementItems: [
    { h: "Ночлег",         body: "12 циновок на сеновале" },
    { h: "Водное помещение", body: "Общая ванная: душ, умывальник, туалет" },
    { h: "Сезон",          body: "Открыто с 1 апреля по 1 ноября" },
    { h: "Donativo",       body: "Свободный вклад: 10–15 € за ночь, оплата на месте" },
    { h: "Хозяин",         body: "Местный хозяин обеспечивает приём и содержание" },
    { h: "Управление",     body: "Ассоциация « Друзья приорства Сен-Леонар »" },
  ],
  contributionsTitle: "Членство · Пожертвования · Услуги",
  contributionsIntro: "Чтобы поддержать жизнь Овчарника помимо donativo, есть несколько путей:",
  contributionsItems: [
    { h: "Членство в ассоциации", body: "Полное 50 € · Пара 30 € · Льготное 20 € · Скауты и Гиды 20 € (одно на отряд/команду/клан)." },
    { h: "Свободное пожертвование", body: "Пожертвование любой суммы — чеком или онлайн." },
    { h: "Услуги", body: "Вклад услугами: содержание, садоводство, приём, переводы, фотографии и т. д. Напишите нам." },
  ],
  taxDeductible: "Ассоциация имеет статус общественной пользы: членские взносы и пожертвования дают французским налоговым резидентам право на налоговую квитанцию (снижение налога на 66 % в пределах 20 % налогооблагаемого дохода, ст. 200 Налогового кодекса).",
  membershipCta: "Вступить / Пожертвовать",
  rulesTitle: "Правила овчарника",
  rulesIntro: "Гостеприимство следует традиции паломнических приютов. Чтобы сохранить покой места и уважение к каждому, вот правила, разделяемые хозяевами и паломниками.",
  rulesSections: [
    { h: "Приём и прибытие", items: [
      "Обязательная предварительная заявка через форму с подтверждением членства.",
      "Прибытие с 16 ч, отъезд до 10 ч следующего утра.",
      "Удостоверение личности при прибытии для журнала гостеприимства.",
      "Срок пребывания в принципе ограничен 2 ночами подряд, кроме особой договорённости.",
    ]},
    { h: "Совместная жизнь", items: [
      "Тишина соблюдается с 22 ч до 7 ч в честь монашеской традиции.",
      "Сеновал — общее место ночлега на циновках; каждый заботится о скромности и покое соседей.",
      "Ванная, коридоры и кухня — общие пространства: убираются после использования.",
      "Обувь оставляют у входа. Паломнические посохи — в стойке для посохов.",
    ]},
    { h: "Кухня, питание, гигиена", items: [
      "Общая кухня свободна для пользования — с уважением к другим.",
      "Посуда моется, сушится и убирается сразу после еды.",
      "Соблюдается раздельный сбор (контейнеры на месте).",
      "Крепкого алкоголя в общих помещениях нет; столовое вино в умеренных количествах допустимо.",
    ]},
    { h: "Обязательства паломника", items: [
      "Уважать духовное призвание места — приорство и овчарник не являются туристическим жильём.",
      "Просто участвовать в общей жизни (посуда, уборка, мелкие дела).",
      "Незамедлительно сообщать хозяину о любых проблемах (оборудование, санитария, безопасность).",
      "Никаких животных внутри (кроме собак-поводырей), курение в помещениях запрещено.",
    ]},
    { h: "Donativo и пожертвования", items: [
      "Стоимость ночи не фиксирована. Свободный вклад: 10–15 € за ночь — в предусмотренную для этого коробку.",
      "Любое пожертвование, наличными или чеком, по запросу даёт право на налоговую квитанцию (ст. 200 NК).",
      "Услуги овчарнику (уход, приём, переводы, фотографии) также приветствуются.",
    ]},
    { h: "Безопасность", items: [
      "Огнетушители и план эвакуации вывешены.",
      "Аварийный выход на втором этаже.",
      "Экстренные номера: 15 (SAMU) · 18 (Пожарные) · 112 (Европа).",
    ]},
    { h: "Нарушение", items: [
      "Любое нарушение этих правил может повлечь немедленный отъезд паломника по усмотрению хозяина и Совета ассоциации.",
    ]},
  ],
  rulesShow: "Прочитать правила",
  rulesHide: "Скрыть правила",
  partnersTitle: "Партнёры и сети",
  partners: [
    "Ассоциация « Пути Мон-Сен-Мишель »",
    "Аббатства Нормандии",
    "Европейская федерация святого Леонарда",
    "Аббатство Сан-Леонардо-ди-Сипонто (Фоджа, Италия)",
  ],
  budgetTitle: "Бюджет реставрации",
  budgetTotal: "32 000 €",
  budgetItems: [
    { lbl: "Соломенная крыша",          v: "12 000 €" },
    { lbl: "Стропильная конструкция",    v: "5 000 €" },
    { lbl: "Ванная (душ, умывальник, WC)", v: "4 000 €" },
    { lbl: "Кухня и оборудование",       v: "4 000 €" },
    { lbl: "Циновки и ночлег (сеновал)", v: "4 000 €" },
    { lbl: "Прочие работы",              v: "3 000 €" },
  ],
  formKicker: "Гостеприимство в овчарнике",
  formTitle: "Заявка на пребывание",
  formIntro: "Заявите о вашем интересе к пребыванию в Паломническом овчарнике. Наш хозяин свяжется с вами для подтверждения наличия мест. Вместимость: 12 циновок на сеновале.",
  conditionsTitle: "Условия приёма",
  conditions: [
    "Быть членом Друзей приорства Сен-Леонар.",
    "Гиды и скауты: одно членство на отряд, команду или клан.",
    "Взрослые: индивидуальное членство для каждого совершеннолетнего участника.",
    "Максимальная вместимость: 12 человек (12 циновок на сеновале, одна общая ванная).",
  ],
  fields: {
    firstName: "Имя", lastName: "Фамилия", email: "Эл. почта", phone: "Телефон (необязательно)",
    group: "Группа / Отряд / Команда / Клан", groupHelp: "Оставьте пустым для индивидуального или семейного пребывания",
    nbPeople: "Количество человек", nbNights: "Количество ночей",
    arrivalDate: "Желаемая дата прибытия (необязательно)",
    notes: "Примечания (необязательно)",
    member: "Подтверждаю, что являюсь членом Друзей приорства Сен-Леонар (или что моя группа состоит в ассоциации).",
  },
  cta: "Отправить заявку",
  sending: "Отправка…",
  successTitle: "Заявка получена, благодарим!",
  successBody: "Наш хозяин свяжется с вами в ближайшее время. Письмо с подтверждением только что отправлено.",
  capacityWarn: "Количество человек не может превышать 12 (вместимость сеновала).",
  donativoBadge: "DONATIVO · 10–15 €/ночь ориентировочно",
};
// Chinese (Simplified) — form-critical override of FR
T.zh = {
  ...T.fr,
  kicker: "接待 · 圣米歇尔山朝圣之路",
  title: "圣玛格丽特朝圣者牧场",
  subtitle: "圣米歇尔山朝圣之路上最后一座接待所重新开放",
  formKicker: "牧场接待",
  formTitle: "住宿申请",
  formIntro: "请表达您在朝圣者牧场住宿的意愿。我们的接待人将与您联系以确认是否有空位。容量:干草棚内12张草垫。",
  conditionsTitle: "接待条件",
  conditions: [
    "须为圣莱奥纳尔修道院之友协会会员。",
    "童军和向导:每队、组或团仅需一份会籍。",
    "成人:每位成年参与者均需个人会籍。",
    "最大容量:12人(干草棚12张草垫,共用一间浴室)。",
  ],
  fields: {
    firstName: "名", lastName: "姓", email: "电子邮件", phone: "电话(可选)",
    group: "团体 / 巡逻队 / 小组 / 团", groupHelp: "个人或家庭住宿请留空",
    nbPeople: "人数", nbNights: "夜数",
    arrivalDate: "希望到达日期(可选)",
    notes: "备注(可选)",
    member: "我证明本人是圣莱奥纳尔修道院之友的会员(或本团体是会员)。",
  },
  cta: "提交申请", sending: "提交中…",
  successTitle: "已收到您的申请,谢谢!",
  successBody: "我们的接待人将很快与您联系。确认邮件已发送给您。",
  capacityWarn: "人数不能超过12人(干草棚容量)。",
  donativoBadge: "DONATIVO · 每晚10–15 € 建议",
};
// Polish — form-critical override of FR
T.pl = {
  ...T.fr,
  kicker: "Gościnność · Droga do Mont saint Michel",
  title: "Aprysk pielgrzymi św. Małgorzaty",
  subtitle: "Ponowne otwarcie ostatniej Gościnności na Drodze do Mont saint Michel",
  formKicker: "Gościnność w aprysku",
  formTitle: "Prośba o pobyt",
  formIntro: "Wyraź zainteresowanie pobytem w Aprysku pielgrzymim. Nasz gospodarz skontaktuje się z Tobą, aby potwierdzić dostępność. Pojemność: 12 mat na strychu.",
  conditionsTitle: "Warunki przyjęcia",
  conditions: [
    "Być członkiem Przyjaciół Klasztoru św. Leonarda.",
    "Harcerze i skauci: wystarczy jedno członkostwo na zastęp, drużynę lub krąg.",
    "Dorośli: indywidualne członkostwo wymagane dla każdego pełnoletniego uczestnika.",
    "Maksymalna pojemność: 12 osób (12 mat na strychu, jedna wspólna łazienka).",
  ],
  fields: {
    firstName: "Imię", lastName: "Nazwisko", email: "E-mail", phone: "Telefon (opcjonalnie)",
    group: "Grupa / Zastęp / Drużyna / Krąg", groupHelp: "Pozostaw puste dla pobytu indywidualnego lub rodzinnego",
    nbPeople: "Liczba osób", nbNights: "Liczba nocy",
    arrivalDate: "Pożądana data przyjazdu (opcjonalnie)",
    notes: "Uwagi (opcjonalnie)",
    member: "Oświadczam, że jestem członkiem Przyjaciół Klasztoru św. Leonarda (lub że moja grupa jest członkiem).",
  },
  cta: "Wyślij prośbę", sending: "Wysyłanie…",
  successTitle: "Prośba otrzymana, dziękujemy!",
  successBody: "Nasz gospodarz wkrótce się z Tobą skontaktuje. Właśnie wysłaliśmy do Ciebie e-mail potwierdzający.",
  capacityWarn: "Liczba osób nie może przekroczyć 12 (pojemność strychu).",
  donativoBadge: "DONATIVO · 10–15 €/noc orientacyjnie",
};
// Ukrainian + Romanian — minimal form-critical overrides (rest falls back to FR)
T.uk = {
  ...T.fr,
  kicker: "Гостинність · Шлях до Мон-Сен-Мішеля",
  title: "Паломницька вівчарня святої Маргарити",
  subtitle: "Повторне відкриття останньої Гостинності на Шляху до Мон-Сен-Мішеля",
  formKicker: "Гостинність у вівчарні",
  formTitle: "Заявка на перебування",
  formIntro: "Висловіть зацікавлення перебуванням у Паломницькій вівчарні. Наш господар зв'яжеться з вами для підтвердження. Місткість: 12 матів на горищі.",
  conditionsTitle: "Умови прийому",
  conditions: [
    "Бути членом Друзів пріорства Сен-Леонар.",
    "Скаути та провідники: одне членство на патруль, команду або клан.",
    "Дорослі: індивідуальне членство для кожного повнолітнього учасника.",
    "Максимальна місткість: 12 осіб (12 матів на горищі, одна спільна ванна).",
  ],
  fields: {
    firstName: "Ім'я", lastName: "Прізвище", email: "Ел. пошта", phone: "Телефон (необов'язково)",
    group: "Група / Патруль / Команда / Клан", groupHelp: "Залиште порожнім для індивідуального або сімейного перебування",
    nbPeople: "Кількість осіб", nbNights: "Кількість ночей",
    arrivalDate: "Бажана дата прибуття (необов'язково)",
    notes: "Примітки (необов'язково)",
    member: "Підтверджую, що я є членом Друзів пріорства Сен-Леонар (або що моя група є членом).",
  },
  cta: "Надіслати заявку", sending: "Надсилання…",
  successTitle: "Заявку отримано, дякуємо!",
  successBody: "Наш господар зв'яжеться з вами найближчим часом. Лист із підтвердженням щойно надіслано.",
  capacityWarn: "Кількість осіб не може перевищувати 12 (місткість горища).",
  donativoBadge: "DONATIVO · 10–15 €/ніч орієнтовно",
};
T.ro = {
  ...T.fr,
  kicker: "Ospitalitate · Drumul către Mont-Saint-Michel",
  title: "Stâna pelerinilor a sfintei Margareta",
  subtitle: "Redeschiderea ultimei Ospitalități pe Drumul către Mont-Saint-Michel",
  formKicker: "Ospitalitate la stână",
  formTitle: "Cerere de cazare",
  formIntro: "Exprimați-vă interesul pentru o ședere la Stâna pelerinilor. Gazda noastră vă va contacta pentru a confirma disponibilitatea. Capacitate: 12 saltele în pod.",
  conditionsTitle: "Condiții de primire",
  conditions: [
    "Să fiți membru al Prietenilor Mănăstirii sfântului Leonard.",
    "Cercetași și ghizi: o singură calitate de membru pe patrulă, echipă sau clan.",
    "Adulți: calitate de membru individuală necesară pentru fiecare participant major.",
    "Capacitate maximă: 12 persoane (12 saltele în pod, o singură baie comună).",
  ],
  fields: {
    firstName: "Prenume", lastName: "Nume", email: "E-mail", phone: "Telefon (opțional)",
    group: "Grup / Patrulă / Echipă / Clan", groupHelp: "Lăsați gol pentru o ședere individuală sau de familie",
    nbPeople: "Număr de persoane", nbNights: "Număr de nopți",
    arrivalDate: "Data dorită de sosire (opțional)",
    notes: "Observații (opțional)",
    member: "Atest că sunt membru al Prietenilor Mănăstirii sfântului Leonard (sau că grupul meu este membru).",
  },
  cta: "Trimite cererea", sending: "Se trimite…",
  successTitle: "Cerere primită, mulțumim!",
  successBody: "Gazda noastră vă va contacta în curând. Tocmai am trimis un e-mail de confirmare.",
  capacityWarn: "Numărul de persoane nu poate depăși 12 (capacitatea podului).",
  donativoBadge: "DONATIVO · 10–15 €/noapte orientativ",
};

const EMPTY = {
  first_name: "", last_name: "", email: "", phone: "",
  group_name: "", nb_people: 1, nb_nights: 1,
  arrival_date: "", notes: "", member_attestation: false,
};

export default function Bergerie() {
  const { locale } = useI18n();
  const tr = T[locale] || T.fr;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [rulesOpen, setRulesOpen] = useState(false);

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const nbP = Number(form.nb_people);
    if (nbP > MAX_BEDS) { setError(tr.capacityWarn); return; }
    setBusy(true);
    try {
      await api.post("/bergerie/request", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        group_name: form.group_name.trim(),
        nb_people: nbP,
        nb_nights: Number(form.nb_nights),
        arrival_date: form.arrival_date,
        notes: form.notes,
        member_attestation: !!form.member_attestation,
        locale,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="bergerie-page">
      <SeoHead titles={SEO_TITLES} descriptions={SEO_DESC} image="/photos/prieure-meadow.jpg"
        keywords="bergerie pèlerine, hébergement insolite baie du Mont-Saint-Michel, grèves de Vains, hébergement Normandie, séjour nature, traversée de la baie, hospitalité, Mont-Saint-Michel" />
      {/* HERO */}
      <section className="relative" style={{ height: "55vh", minHeight: 380 }}>
        <img src="/photos/prieure-meadow.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(20,16,12,0.55), rgba(20,16,12,0.40))" }} />
        <div className="container-prieure relative h-full flex flex-col justify-end pb-12">
          <div className="font-caps text-[0.7rem] mb-3" style={{ color: "var(--gold)", letterSpacing: "0.28em" }}>{tr.kicker}</div>
          <h1 className="font-display text-4xl sm:text-6xl text-white mb-3">{tr.title}</h1>
          <p className="font-display italic text-lg sm:text-2xl text-white" style={{ maxWidth: "60ch" }}>{tr.subtitle}</p>
        </div>
      </section>

      {/* Quote */}
      <section className="py-14" style={{ background: "var(--parchment)" }}>
        <div className="container-prieure max-w-3xl text-center">
          <Ornament className="mb-6" />
          <p className="font-display italic text-2xl" style={{ color: "var(--ink-2)" }}>« {tr.introQuote} »</p>
        </div>
      </section>

      {/* Context */}
      <section className="py-20" style={{ background: "white" }}>
        <div className="container-prieure grid grid-cols-1 md:grid-cols-12 gap-10 items-start max-w-6xl mx-auto">
          <div className="md:col-span-5">
            <div className="font-caps text-[0.62rem] mb-3" style={{ color: "var(--gold)", letterSpacing: "0.26em" }}>◇ {tr.context.kicker} ◇</div>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight">{tr.context.title}</h2>
          </div>
          <div className="md:col-span-7">
            <p className="font-body text-lg" style={{ color: "var(--ink-2)", lineHeight: 1.7 }}>{tr.context.body}</p>
          </div>
        </div>
      </section>

      {/* History */}
      <section className="py-20" style={{ background: "var(--parchment)" }}>
        <div className="container-prieure grid grid-cols-1 md:grid-cols-12 gap-10 items-start max-w-6xl mx-auto">
          <div className="md:col-span-5 md:order-2">
            <div className="font-caps text-[0.62rem] mb-3" style={{ color: "var(--gold)", letterSpacing: "0.26em" }}>◇ {tr.history.kicker} ◇</div>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight">{tr.history.title}</h2>
          </div>
          <div className="md:col-span-7 md:order-1">
            <p className="font-body text-lg" style={{ color: "var(--ink-2)", lineHeight: 1.7 }}>{tr.history.body}</p>
          </div>
        </div>
      </section>

      {/* Fonctionnement */}
      <section className="py-20" style={{ background: "var(--ink)", color: "var(--parchment)" }}>
        <div className="container-prieure max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl">{tr.fonctionnementTitle}</h2>
            <div className="font-caps inline-block mt-4 text-[0.62rem] px-3 py-1 border"
                 style={{ color: "var(--gold)", borderColor: "var(--gold)", letterSpacing: "0.22em" }}>
              {tr.donativoBadge}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {tr.fonctionnementItems.map((it, i) => (
              <div key={i} className="p-6" style={{ background: "rgba(245,240,228,0.05)", border: "1px solid rgba(245,240,228,0.10)" }}>
                <div className="font-display text-xl mb-2" style={{ color: "var(--gold)" }}>{it.h}</div>
                <div className="font-body text-sm" style={{ color: "rgba(245,240,228,0.85)" }}>{it.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners + Budget */}
      <section className="py-20" style={{ background: "var(--parchment)" }}>
        <div className="container-prieure max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <h3 className="font-display text-2xl mb-5">{tr.partnersTitle}</h3>
            <ul className="space-y-3">
              {tr.partners.map((p, i) => (
                <li key={i} className="flex items-start gap-3 font-body" style={{ color: "var(--ink-2)" }}>
                  <span className="font-display mt-1" style={{ color: "var(--gold)" }}>◇</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-display text-2xl mb-5">{tr.budgetTitle}</h3>
            <div className="font-display text-4xl mb-5" style={{ color: "var(--cinnabar)" }}>{tr.budgetTotal}</div>
            <ul className="space-y-2">
              {tr.budgetItems.map((b, i) => (
                <li key={i} className="flex justify-between font-body text-sm pb-2"
                    style={{ color: "var(--ink-2)", borderBottom: "1px solid var(--border)" }}>
                  <span>{b.lbl}</span>
                  <span className="font-display">{b.v}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Contributions & tax-deductible */}
      <section className="py-20" style={{ background: "white" }} data-testid="bergerie-contributions">
        <div className="container-prieure max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Ornament className="mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl">{tr.contributionsTitle}</h2>
            <p className="font-display italic mt-4 max-w-2xl mx-auto" style={{ color: "var(--ink-2)" }}>{tr.contributionsIntro}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tr.contributionsItems.map((it, i) => (
              <article key={i} data-testid={`bergerie-contrib-${i}`}
                       className="p-7 border" style={{ borderColor: "var(--border)" }}>
                <h3 className="font-display text-xl mb-3">{it.h}</h3>
                <p className="font-body text-sm" style={{ color: "var(--ink-2)" }}>{it.body}</p>
              </article>
            ))}
          </div>
          <div className="mt-8 p-5 max-w-3xl mx-auto" data-testid="bergerie-tax-notice"
               style={{ background: "var(--parchment)", borderLeft: "3px solid var(--cinnabar)" }}>
            <p className="font-body text-sm" style={{ color: "var(--ink-2)" }}>
              <strong>★</strong> {tr.taxDeductible}
            </p>
          </div>
          <div className="mt-8 text-center">
            <a href="/association#adhesion" data-testid="bergerie-membership-cta"
               className="btn btn-primary"
               style={{ fontSize: "0.74rem", padding: "0.85rem 1.8rem", letterSpacing: "0.18em" }}>
              {tr.membershipCta} →
            </a>
          </div>
        </div>
      </section>

      {/* Règlement intérieur (collapsible) */}
      <section className="py-20" style={{ background: "var(--parchment)" }} data-testid="bergerie-rules">
        <div className="container-prieure max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className="font-caps text-[0.62rem] mb-3" style={{ color: "var(--gold)", letterSpacing: "0.26em" }}>◇ RÈGLEMENT ◇</div>
            <h2 className="font-display text-3xl sm:text-4xl">{tr.rulesTitle}</h2>
            <p className="font-display italic mt-4 max-w-2xl mx-auto" style={{ color: "var(--ink-2)" }}>{tr.rulesIntro}</p>
          </div>
          <div className="text-center">
            <button type="button" onClick={() => setRulesOpen((v) => !v)}
                    data-testid="bergerie-rules-toggle"
                    className="btn btn-outline"
                    style={{ fontSize: "0.72rem", padding: "0.75rem 1.6rem", letterSpacing: "0.18em" }}>
              {rulesOpen ? tr.rulesHide : tr.rulesShow}
            </button>
          </div>
          {rulesOpen && (
            <div className="mt-10 space-y-8 bg-white p-8 border" data-testid="bergerie-rules-body"
                 style={{ borderColor: "var(--border)" }}>
              {tr.rulesSections.map((s, i) => (
                <div key={i}>
                  <h4 className="font-display text-lg mb-3" style={{ color: "var(--ink)" }}>{i + 1}. {s.h}</h4>
                  <ul className="space-y-2">
                    {s.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-3 font-body text-[0.92rem]"
                          style={{ color: "var(--ink-2)" }}>
                        <span className="font-display" style={{ color: "var(--gold)", minWidth: 12 }}>·</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FORM */}
      <section className="py-20" style={{ background: "white" }} data-testid="bergerie-form-section">
        <div className="container-prieure max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="font-caps text-[0.62rem] mb-3" style={{ color: "var(--gold)", letterSpacing: "0.26em" }}>◇ {tr.formKicker} ◇</div>
            <h2 className="font-display text-3xl sm:text-4xl">{tr.formTitle}</h2>
            <p className="font-display italic mt-4 max-w-xl mx-auto" style={{ color: "var(--ink-2)" }}>{tr.formIntro}</p>
          </div>

          {/* Conditions */}
          <aside className="p-6 mb-10" style={{ background: "var(--parchment)", borderLeft: "3px solid var(--gold)" }}>
            <div className="font-caps text-[0.6rem] mb-3" style={{ color: "var(--gold)", letterSpacing: "0.22em" }}>{tr.conditionsTitle.toUpperCase()}</div>
            <ul className="space-y-2">
              {tr.conditions.map((c, i) => (
                <li key={i} className="flex items-start gap-3 font-body text-[0.92rem]" style={{ color: "var(--ink-2)" }}>
                  <span className="font-display" style={{ color: "var(--cinnabar)" }}>•</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </aside>

          {done ? (
            <div data-testid="bergerie-form-success" className="p-10 text-center" style={{ background: "var(--parchment)" }}>
              <div style={{ color: "var(--gold)", fontSize: "2.5rem" }}>◇</div>
              <h3 className="font-display text-2xl mt-3">{tr.successTitle}</h3>
              <p className="font-body mt-3" style={{ color: "var(--ink-2)" }}>{tr.successBody}</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="bergerie-form" className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label={tr.fields.firstName} required>
                <input data-testid="bergerie-first-name" required value={form.first_name} onChange={set("first_name")} className="form-input" />
              </Field>
              <Field label={tr.fields.lastName} required>
                <input data-testid="bergerie-last-name" required value={form.last_name} onChange={set("last_name")} className="form-input" />
              </Field>
              <Field label={tr.fields.email} required>
                <input data-testid="bergerie-email" type="email" required value={form.email} onChange={set("email")} className="form-input" />
              </Field>
              <Field label={tr.fields.phone}>
                <input data-testid="bergerie-phone" value={form.phone} onChange={set("phone")} className="form-input" />
              </Field>
              <Field label={tr.fields.group} className="sm:col-span-2" help={tr.fields.groupHelp}>
                <input data-testid="bergerie-group" value={form.group_name} onChange={set("group_name")} className="form-input" />
              </Field>
              <Field label={tr.fields.nbPeople} required>
                <input data-testid="bergerie-nb-people" type="number" min="1" max={MAX_BEDS} required value={form.nb_people} onChange={set("nb_people")} className="form-input" />
              </Field>
              <Field label={tr.fields.nbNights} required>
                <input data-testid="bergerie-nb-nights" type="number" min="1" max="30" required value={form.nb_nights} onChange={set("nb_nights")} className="form-input" />
              </Field>
              <Field label={tr.fields.arrivalDate} className="sm:col-span-2">
                <input data-testid="bergerie-arrival" type="date" value={form.arrival_date} onChange={set("arrival_date")} className="form-input" />
              </Field>
              <Field label={tr.fields.notes} className="sm:col-span-2">
                <textarea data-testid="bergerie-notes" rows="3" value={form.notes} onChange={set("notes")} className="form-input" />
              </Field>

              <label className="sm:col-span-2 flex items-start gap-3 py-3 cursor-pointer">
                <input data-testid="bergerie-member-attestation" type="checkbox" required checked={form.member_attestation} onChange={set("member_attestation")}
                       className="mt-1" style={{ width: 18, height: 18, accentColor: "var(--cinnabar)" }} />
                <span className="font-body text-[0.92rem]" style={{ color: "var(--ink-2)" }}>{tr.fields.member}</span>
              </label>

              {error && <div className="sm:col-span-2 text-sm" style={{ color: "var(--cinnabar)" }} data-testid="bergerie-form-error">{error}</div>}

              <div className="sm:col-span-2 flex justify-center mt-3">
                <button type="submit" data-testid="bergerie-submit" disabled={busy}
                        className="btn btn-primary"
                        style={{ fontSize: "0.78rem", padding: "0.95rem 2.4rem", letterSpacing: "0.18em" }}>
                  {busy ? tr.sending : tr.cta}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, required, className = "", help }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-caps text-[0.6rem] block mb-1.5" style={{ color: "var(--ink-2)", letterSpacing: "0.2em" }}>
        {label} {required && <span style={{ color: "var(--cinnabar)" }}>*</span>}
      </span>
      {children}
      {help && <span className="font-body text-xs block mt-1" style={{ color: "var(--muted)" }}>{help}</span>}
    </label>
  );
}
